import React, { useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import { ReactComponent as GoogleIcon } from './icons/google.svg';
import { getDoc, doc } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { notifyAuthStateChange, getApiUrl, getDatabaseRequestHeaders } from '../api';
import DatabaseModeToggle from './DatabaseModeToggle';

function LoginModal({ show, onHide, onLoginSuccess, databaseMode, onDatabaseModeChange }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);

  // Helper function to store token and notify auth state change
  const handleSuccessfulAuth = async (user) => {
    try {
      const token = await user.getIdToken();
      localStorage.setItem('authToken', token);
      notifyAuthStateChange();
    } catch (error) {
      console.error('Error getting auth token:', error);
    }
  };

  // Helper function to create initial user profile
  const createInitialProfile = async (user) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/user/${user.uid}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getDatabaseRequestHeaders(),
        },
        body: JSON.stringify({
          email: user.email,
          name: '',
          practice: '',
          role: '',
          emr: ''
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create initial profile');
      }

      console.log('Initial profile created successfully');
    } catch (error) {
      console.error('Error creating initial profile:', error);
    }
  };

  // Helper function to check if profile exists
  const checkProfileExists = async (userId) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/user/${userId}`, {
        headers: {
          ...getDatabaseRequestHeaders(),
        },
      });
      return response.ok;
    } catch (error) {
      console.error("Error checking profile:", error);
      return false;
    }
  };

  // Handle Google login
  const handleGoogleLogin = async () => {
    try {
  
      const result = await signInWithPopup(auth, googleProvider);
      
      // Check if the email is from Springhouse
      console.log(result.user.email)
      if (!result.user.email.endsWith('@springhouseanalytics.com') && !result.user.email.endsWith('@delortho.com')) {
        // Delete the user account if it was created
        try {
          await result.user.delete();
          console.log('Deleted unauthorized user account');
        } catch (deleteError) {
          console.error('Error deleting unauthorized account:', deleteError);
        }
        
        // Sign out the user
        await auth.signOut();
        setLoginError('Only @springhouseanalytics.com and @delortho.com email addresses are allowed');
        return;
      }
      
      // Store token and notify auth state change
      await handleSuccessfulAuth(result.user);
      
      // Close modal and notify success immediately
      handleClose();
      onLoginSuccess(result.user);
      
      // Check profile existence after closing modal
      const hasProfile = await checkProfileExists(result.user.uid);
      if (!hasProfile) {
        console.log("No profile found, creating initial profile...");
        await createInitialProfile(result.user);
        setShowProfilePrompt(true);
      }
    } catch (error) {
      console.error("Error signing in with Google:", error);
      setLoginError(error.message);
    }
  };

  // Handle Email/Password login
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    try {
      // Check if the email is from Springhouse before attempting login
      if (!email.endsWith('@springhouseanalytics.com') && !email.endsWith('@delortho.com')) {
        setLoginError('Only @springhouseanalytics.com and @delortho.com email addresses are allowed');
        return;
      }

      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Store token and notify auth state change
      await handleSuccessfulAuth(result.user);
      
      // Close modal and notify success immediately
      handleClose();
      onLoginSuccess(result.user);
      
      // Check profile existence after closing modal
      const hasProfile = await checkProfileExists(result.user.uid);
      if (!hasProfile) {
        console.log("No profile found, creating initial profile...");
        await createInitialProfile(result.user);
        setShowProfilePrompt(true);
      }
    } catch (error) {
      console.error("Error signing in with email/password:", error);
      setLoginError(error.message);
    }
  };

  // Helper function to check if profile is complete
  const isProfileComplete = (profile) => {
    return profile && profile.name && profile.practice && profile.role && profile.ehr;
  };

  {/* Handle Email/Password signup
  const handleEmailSignup = async () => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Store token and notify auth state change
      await handleSuccessfulAuth(result.user);
      
      // Close modal and notify success immediately
      handleClose();
      onLoginSuccess(result.user);
      
      // Create profile after closing modal
      await createInitialProfile(result.user);
      setShowProfilePrompt(true);
    } catch (error) {
      console.error("Error creating account:", error);
      setLoginError(error.message);
    }
  }; */}

  // Handle modal close and reset form
  const handleClose = () => {
    setEmail('');
    setPassword('');
    setLoginError('');
    setShowProfilePrompt(false);
    onHide();
  };

  if (showProfilePrompt) {
    return (
      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header>
          <Modal.Title>Complete Your Profile</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-4">
            Please take a moment to complete your profile information. This will help us provide you with a better experience.
          </p>
          <Button 
            variant="primary" 
            onClick={() => {
              handleClose();
              window.location.href = '/profile';
            }}
          >
            Go to Profile
          </Button>
          <Button 
            variant="link" 
            onClick={handleClose}
            className="ms-2"
          >
            I'll do this later
          </Button>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Login</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleEmailLogin}>
          {onDatabaseModeChange && (
            <Form.Group className="mb-3">
              <Form.Label>Database</Form.Label>
              <DatabaseModeToggle
                value={databaseMode}
                onChange={onDatabaseModeChange}
              />
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Email address</Form.Label>
            <Form.Control
              type="email"
              placeholder="Enter email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Password</Form.Label>
            <Form.Control
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </Form.Group>

          {loginError && (
            <div className="alert alert-danger">{loginError}</div>
          )}

          <div className="d-grid gap-2">
            <Button variant="primary" type="submit">
              Login
            </Button>
            {/* Sign Up button commented out
            <Button variant="secondary" onClick={handleEmailSignup}>
              Sign Up
            </Button>
            */}
            <div className="text-center my-3">or</div>
            <GoogleIcon 
              onClick={handleGoogleLogin}
              style={{ 
                cursor: 'pointer',
                width: '100%',
                height: 'auto',
                maxHeight: '40px'
              }} 
            />
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default LoginModal; 